/* eslint-disable camelcase */
import React, { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import {
  Box,
  Paper,
  TextField,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Tooltip,
  Chip,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  IconButton,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { api, showWarning, showSuccess } from "../../../api"
import normalFormatDate from "../../utils/date/DDMMYYYFormatDate"
import Swal from "sweetalert2"

// Íconos estándar
import LockIcon from "@mui/icons-material/Lock"
import SyncAltIcon from "@mui/icons-material/SyncAlt"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import VisibilityIcon from "@mui/icons-material/Visibility"
import PersonAddIcon from "@mui/icons-material/PersonAdd"
import GroupIcon from "@mui/icons-material/Group"
import HistoryIcon from "@mui/icons-material/History"
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" }, error: { main: "#d32f2f" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

// Estilo para habilitar modo multilínea en los DataGrids con mucho texto
const multilineGridSx = {
  "& .MuiDataGrid-cell": {
    whiteSpace: "normal",
    wordWrap: "break-word",
    py: 1.5,
    lineHeight: "1.4",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f0f4f8",
    color: "#196C87",
    fontWeight: "bold",
  },
}

const formatToSQLDatetime = (dateString, isTime = false) => {
  if (!dateString) return null
  const d = new Date(dateString)
  if (isNaN(d.getTime())) return dateString

  const hh = String(d.getUTCHours()).padStart(2, "0")
  const min = String(d.getUTCMinutes()).padStart(2, "0")
  const ss = String(d.getUTCSeconds()).padStart(2, "0")

  if (isTime) {
    return `1900-01-01 ${hh}:${min}:${ss}`
  }

  const yyyy = d.getUTCFullYear()
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0")
  const dd = String(d.getUTCDate()).padStart(2, "0")

  return `${yyyy}-${mm}-${dd} 00:00:00`
}

const extractMessage = (res, defaultMsg) => {
  let msg = res?.data?.data
  if (typeof msg === "object" && msg !== null) {
    msg = msg?.data || msg?.message
  }
  return typeof msg === "string" ? msg : defaultMsg
}

const DetalleTomaFisica = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const selectedMenuInfo = JSON.parse(localStorage.getItem("selectedMenuInfo") || "{}")
  const tomnumero_state = location.state?.tomnumero || ""

  const [rowSelectionModel, setRowSelectionModel] = useState([])
  const [modalAjusteOpen, setModalAjusteOpen] = useState(false)
  const [ajusteForm, setAjusteForm] = useState({
    tracodigo_ingreso: "",
    tracodigo_egreso: "",
    observacion: "AJUSTE POR TOMA FISICA",
  })

  const [modalDetalleOpen, setModalDetalleOpen] = useState(false)
  const [articuloSeleccionado, setArticuloSeleccionado] = useState({ codigo: "", descripcion: "" })
  const [operadorSeleccionado, setOperadorSeleccionado] = useState(null)

  // Estados para el Modal de Historial y Reverso
  const [modalHistorialOpen, setModalHistorialOpen] = useState(false)
  const [historialLecturas, setHistorialLecturas] = useState([])
  const [isFetchingHistorial, setIsFetchingHistorial] = useState(false)

  // --- CONSULTAS (QUERIES) ---

  const fetchTomoCabeceraFresh = async () => {
    const response = await api.post("/TomaFisica/getTomoCabecera", { tomnumero: tomnumero_state })
    return response?.data?.data || response?.data || {}
  }

  const { data: tomoData, isLoading: isLoadingCabecera } = useQuery({
    queryKey: ["getTomoCabeceraDetalle", tomnumero_state],
    queryFn: fetchTomoCabeceraFresh,
    enabled: !!tomnumero_state,
    refetchOnWindowFocus: false,
  })

  const { data: diferenciasData, isLoading: isLoadingDiferencias } = useQuery({
    queryKey: ["getReporteDiferencias", tomnumero_state],
    queryFn: async () => {
      const response = await api.get(`/TomaFisica/getReporteDiferencias?tomnumero=${tomnumero_state}`)
      const rawData = response?.data?.data || response?.data || response || []
      return Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : []
    },
    enabled: !!tomnumero_state,
    refetchOnWindowFocus: false,
  })

  const { data: catalogosTomo, isLoading: isLoadingCatalogos } = useQuery({
    queryKey: ["getCatalogosGeneracionTomo"],
    queryFn: async () => {
      const response = await api.post("/TomaFisica/getCatalogosGeneracionTomo", {})
      return response?.data?.data || {}
    },
    refetchOnWindowFocus: false,
  })

  const { data: desgloseData, isFetching: isFetchingDesglose } = useQuery({
    queryKey: ["getDetalleLotesSeries", tomnumero_state, articuloSeleccionado.codigo],
    queryFn: async () => {
      const response = await api.post("/TomaFisica/getDetalleLotesSeries", {
        tomnumero: tomnumero_state,
        artcodigo: articuloSeleccionado.codigo,
      })
      const rawData = response?.data?.data || response?.data || []
      return Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : []
    },
    enabled: modalDetalleOpen && !!articuloSeleccionado.codigo,
    refetchOnWindowFocus: false,
  })

  const {
    data: cuadrillaData = { list: [], isSupervisor: false },
    isLoading: isLoadingCuadrilla,
    refetch: refetchCuadrilla,
  } = useQuery({
    queryKey: ["getCuadrillaTomo", tomnumero_state],
    queryFn: async () => {
      const response = await api.post("/TomaFisica/getCuadrillaTomo", { tomnumero: tomnumero_state })

      const rawData = response?.data?.data || response?.data || []
      const lista = Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : []

      const isSup =
        response?.is_supervisor === true ||
        response?.data?.is_supervisor === true ||
        response?.data?.data?.is_supervisor === true ||
        String(response?.is_supervisor) === "true" ||
        String(response?.data?.is_supervisor) === "true"

      return {
        list: lista,
        isSupervisor: isSup,
      }
    },
    enabled: !!tomnumero_state,
    refetchOnWindowFocus: false,
  })

  // --- MUTACIONES (MUTATIONS) ---

  const { mutateAsync: mutUpdateCuadrilla, isPending: isUpdatingCuadrilla } = useMutation({
    mutationFn: async (payload) => {
      return await api.post("/TomaFisica/updateCuadrillaTomo", payload)
    },
    onSuccess: async (res) => {
      showSuccess(extractMessage(res, "Cuadrilla actualizada exitosamente"))
      setOperadorSeleccionado(null)
      await refetchCuadrilla()
    },
    onError: (err) => showWarning(err?.response?.data?.message || "Error al actualizar la cuadrilla"),
  })

  const { mutateAsync: mutCerrarConteo, isPending: isClosingConteo } = useMutation({
    mutationFn: async (payload) => {
      return await api.put("/TomaFisica/cerrarConteo", payload)
    },
    onSuccess: async (res) => {
      showSuccess(extractMessage(res, "Conteo cerrado exitosamente"))
      await queryClient.invalidateQueries({ queryKey: ["getTomoCabeceraDetalle"] })
      await queryClient.invalidateQueries({ queryKey: ["getReporteDiferencias"] })
    },
    onError: (err) => showWarning(err?.response?.data?.message || "Error al cerrar conteo"),
  })

  const { mutateAsync: mutGenerarAjustes, isPending: isGeneratingAjustes } = useMutation({
    mutationFn: async (payloadAjuste) => {
      return await api.post("/TomaFisica/generarAjustesContables", payloadAjuste)
    },
    onSuccess: async (res) => {
      showSuccess(extractMessage(res, "Ajustes generados exitosamente"))
      setModalAjusteOpen(false)
      setRowSelectionModel([])
      await queryClient.invalidateQueries({ queryKey: ["getReporteDiferencias"] })
    },
    onError: (err) => showWarning(err?.response?.data?.message || "Error al generar ajustes"),
  })

  const { mutateAsync: mutCerrarDefinitivo, isPending: isClosingDefinitivo } = useMutation({
    mutationFn: async (payload) => {
      return await api.put("/TomaFisica/cerrarTomoDefinitivo", payload)
    },
    onSuccess: async (res) => {
      showSuccess(extractMessage(res, "Tomo finalizado e histórico"))
      await queryClient.invalidateQueries({ queryKey: ["getTomoCabeceraDetalle"] })
    },
    onError: (err) => showWarning(err?.response?.data?.message || "Error al finalizar el tomo"),
  })

  const { mutateAsync: mutReversarLectura, isPending: isReversing } = useMutation({
    mutationFn: async (payload) => {
      return await api.post("/TomaFisica/reversarLecturaTomo", payload)
    },
    onSuccess: async (res) => {
      showSuccess(extractMessage(res, "Reverso aplicado exitosamente."))
      await queryClient.invalidateQueries({ queryKey: ["getReporteDiferencias"] })
      if (articuloSeleccionado.codigo) {
        await fetchHistorial(articuloSeleccionado.codigo)
      }
    },
    onError: (err) => showWarning(err?.response?.data?.message || "Error al reversar el registro."),
  })

  // --- HANDLERS ---
  const handleAddOperador = () => {
    if (!operadorSeleccionado) return showWarning("Debe seleccionar un operador de la lista.")
    mutUpdateCuadrilla({ action: "ADD", tomnumero: tomnumero_state, operario: operadorSeleccionado.id })
  }

  const handleRemoveOperador = (operario) => {
    Swal.fire({
      title: "¿Quitar Operador?",
      text: `Se removerá a ${operario} de la cuadrilla. ¿Desea continuar?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sí, remover",
    }).then((res) => {
      if (res.isConfirmed) {
        mutUpdateCuadrilla({ action: "REMOVE", tomnumero: tomnumero_state, operario })
      }
    })
  }

  const handleCerrarConteo = async () => {
    if (
      window.confirm(
        "¿Está seguro de cerrar el conteo? Se consolidarán las ventas simultáneas y se bloqueará la captura móvil.",
      )
    ) {
      try {
        const freshHeader = await fetchTomoCabeceraFresh()
        await mutCerrarConteo({
          tomnumero: tomnumero_state,
          tomfecmsys: formatToSQLDatetime(freshHeader?.tomfecmsys, false),
          tomhormsys: formatToSQLDatetime(freshHeader?.tomhormsys, true),
        })
      } catch (error) {
        showWarning("No se pudo sincronizar la cabecera del tomo.")
      }
    }
  }

  const handleCierreDefinitivo = async () => {
    if (
      window.confirm("¿Está seguro de finalizar este tomo? Pasará a estado histórico y no podrá ser modificado jamás.")
    ) {
      try {
        const freshHeader = await fetchTomoCabeceraFresh()
        await mutCerrarDefinitivo({
          tomnumero: tomnumero_state,
          tomfecmsys: formatToSQLDatetime(freshHeader?.tomfecmsys, false),
          tomhormsys: formatToSQLDatetime(freshHeader?.tomhormsys, true),
        })
      } catch (error) {
        showWarning("No se pudo sincronizar la cabecera del tomo.")
      }
    }
  }

  const handleAbrirModalAjustes = () => {
    if (rowSelectionModel.length === 0) {
      return showWarning("Seleccione primero en la tabla los artículos que desea ajustar.")
    }
    setModalAjusteOpen(true)
  }

  const handleEjecutarAjustes = () => {
    if (!ajusteForm.tracodigo_ingreso || !ajusteForm.tracodigo_egreso) {
      return showWarning("Debe seleccionar las transacciones de Ingreso y Egreso.")
    }

    const articulosValidos = rowSelectionModel.filter((id) => {
      const row = listaDiferencias.find((r) => r.artcodigo === id)
      return row && Number(row.diferencia) !== 0
    })

    if (articulosValidos.length === 0) {
      return showWarning("Los artículos seleccionados no tienen diferencias (están cuadrados). No se requiere ajuste.")
    }

    const payload = {
      tomnumero: tomnumero_state,
      articulos_ajustar: articulosValidos,
      tracodigo_ingreso: ajusteForm.tracodigo_ingreso,
      tracodigo_egreso: ajusteForm.tracodigo_egreso,
      observacion: ajusteForm.observacion,
    }

    mutGenerarAjustes(payload)
  }

  const handleOpenDesglose = (row) => {
    setArticuloSeleccionado({ codigo: row.artcodigo, descripcion: row.artdescri })
    setModalDetalleOpen(true)
  }

  const handleCloseDesglose = () => {
    setModalDetalleOpen(false)
    setArticuloSeleccionado({ codigo: "", descripcion: "" })
  }

  // Lógica del Historial
  const fetchHistorial = async (artcodigo) => {
    setIsFetchingHistorial(true)
    try {
      const res = await api.post("/TomaFisica/getHistorialLecturasTomo", {
        tomnumero: tomnumero_state,
        artcodigo,
      })
      const payload = res?.data?.data || res?.data || []
      setHistorialLecturas(Array.isArray(payload) ? payload : Array.isArray(payload.data) ? payload.data : [])
    } catch (error) {
      showWarning("Error al consultar el historial de este artículo.")
    } finally {
      setIsFetchingHistorial(false)
    }
  }

  const handleAbrirHistorial = async (row) => {
    setArticuloSeleccionado({ codigo: row.artcodigo, descripcion: row.artdescri })
    setModalHistorialOpen(true)
    await fetchHistorial(row.artcodigo)
  }

  const handleEjecutarReverso = (secuencia, cantidad) => {
    Swal.fire({
      title: "¿Reversar este escaneo?",
      text: `Se inyectará un registro compensatorio de -${cantidad} unidades. Esta acción es auditable.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, reversar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        mutReversarLectura({
          tomnumero: tomnumero_state,
          contsecuencia: secuencia,
        })
      }
    })
  }

  // --- VARIABLES DEFENSIVAS Y PERMISOS ---
  const info = tomoData || {}
  const listaDiferencias = Array.isArray(diferenciasData) ? diferenciasData : []

  // Validamos si al menos 1 registro (en el detalle o cabecera) sugiere Lote/Serie (TomSugLotSer <> 0)
  const tieneSugerenciaLoteSerie =
    listaDiferencias.some((r) => r.TomSugLotSer && Number(r.TomSugLotSer) !== 0) ||
    (info.TomSugLotSer && Number(info.TomSugLotSer) !== 0)

  const transaccionesData = catalogosTomo?.transacciones || []
  const usuariosList = catalogosTomo?.operarios || []

  const transaccionesIngreso = Array.isArray(transaccionesData) ? transaccionesData.filter((t) => t.tipo === "I") : []
  const transaccionesEgreso = Array.isArray(transaccionesData) ? transaccionesData.filter((t) => t.tipo === "E") : []

  const listaDesglose = Array.isArray(desgloseData) ? desgloseData : []

  const cuadrillaList = Array.isArray(cuadrillaData.list) ? cuadrillaData.list : []

  const estadoTomo = info.tomstatus?.trim()
  const canModifyCuadrilla = (estadoTomo === "G" || estadoTomo === "P") && cuadrillaData.isSupervisor

  const isWorking =
    isLoadingCabecera ||
    isLoadingDiferencias ||
    isLoadingCuadrilla ||
    isLoadingCatalogos ||
    isClosingConteo ||
    isGeneratingAjustes ||
    isClosingDefinitivo ||
    isUpdatingCuadrilla ||
    isReversing

  // --- COLUMNAS DEL DATAGRID PRINCIPAL ---
  const columns = [
    { field: "artcodigo", headerName: "Cód. Artículo", width: 150, headerAlign: "center", align: "center" },
    { field: "artdescri", headerName: "Descripción", flex: 1, minWidth: 250 },
    {
      field: "tomsaldo",
      headerName: "Stock Sistema",
      width: 130,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => <b>{Number(params.value || 0).toFixed(2)}</b>,
    },
    {
      field: "total_contado",
      headerName: "Conteo Móvil",
      width: 130,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => <b style={{ color: "#196C87" }}>{Number(params.value || 0).toFixed(2)}</b>,
    },
    {
      field: "diferencia",
      headerName: "Diferencia",
      width: 110,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        const val = Number(params.value || 0)
        const color = val > 0 ? "orange" : val < 0 ? "red" : "green"
        return <b style={{ color }}>{val.toFixed(2)}</b>
      },
    },
    {
      field: "estado_dif",
      headerName: "Estado",
      width: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        let color = "default"
        if (params.value === "SOBRANTE") color = "warning"
        if (params.value === "FALTANTE") color = "error"
        if (params.value === "CUADRADO") color = "success"
        return <Chip label={params.value || "N/A"} color={color} size="small" sx={{ fontWeight: "bold" }} />
      },
    },
    {
      field: "TomSugLotSer",
      headerName: "Sugiere L/S",
      width: 110,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const val = Number(params.value || 0)
        return val !== 0 ? (
          <Chip label="SÍ" color="info" size="small" sx={{ fontWeight: "bold" }} />
        ) : (
          <Typography variant="body2" color="textSecondary">
            NO
          </Typography>
        )
      },
    },
    {
      field: "acciones",
      type: "actions",
      headerName: "Acciones",
      width: 110,
      getActions: (params) => {
        const acciones = []

        acciones.push(
          <GridActionsCellItem
            key={`hist-${params.row.artcodigo}`}
            icon={<HistoryIcon color="secondary" />}
            label="Historial de Escaneos"
            onClick={() => handleAbrirHistorial(params.row)}
            showInMenu={false}
          />,
        )

        const requiereLote = params.row.artlote && params.row.artlote !== 0
        const requiereSerie =
          (params.row.artserie && params.row.artserie !== 0) ||
          (params.row.artseriedesp && params.row.artseriedesp !== 0)

        if (requiereLote || requiereSerie) {
          acciones.push(
            <GridActionsCellItem
              key={`view-${params.row.artcodigo}`}
              icon={<VisibilityIcon color="primary" />}
              label="Ver Detalles (Lote/Serie)"
              onClick={() => handleOpenDesglose(params.row)}
              showInMenu={false}
            />,
          )
        }

        return acciones
      },
    },
  ]

  // --- COLUMNAS DEL DATAGRID SECUNDARIO (DESGLOSE) ---
  const columnsDesglose = [
    {
      field: "contlote",
      headerName: "Número de Lote",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => <span>{params.value || "N/A"}</span>,
    },
    {
      field: "contcaducidad",
      headerName: "Fec. Caducidad",
      width: 130,
      renderCell: (params) => <span>{params.value ? normalFormatDate(params.value) : "N/A"}</span>,
    },
    {
      field: "contserie",
      headerName: "Número de Serie",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => <span>{params.value || "N/A"}</span>,
    },
    {
      field: "contcantidad",
      headerName: "Cant. Contada",
      width: 130,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => <b style={{ color: "#196C87" }}>{Number(params.value || 0).toFixed(2)}</b>,
    },
  ]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* TOP BAR / MÁQUINA DE ESTADOS */}
        <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
          {info.tomstatus === "P" && (
            <Tooltip title="Cerrar Conteo Móvil y Consolidar Ventas">
              <Button
                variant="contained"
                color="secondary"
                startIcon={<LockIcon />}
                onClick={handleCerrarConteo}
                disabled={isWorking}
              >
                Cerrar Conteo
              </Button>
            </Tooltip>
          )}

          {info.tomstatus === "C" && (
            <>
              <Tooltip title="Generar Egresos/Ingresos por Ajuste para los ítems seleccionados">
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<SyncAltIcon />}
                  onClick={handleAbrirModalAjustes}
                  disabled={isWorking}
                >
                  Ejecutar Ajustes
                </Button>
              </Tooltip>
              <Tooltip title="Bloquear Tomo Históricamente">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleCierreDefinitivo}
                  disabled={isWorking}
                >
                  Finalizar Tomo
                </Button>
              </Tooltip>
            </>
          )}

          {info.tomstatus === "F" && (
            <Chip
              label="INVENTARIO FINALIZADO - SOLO LECTURA"
              color="success"
              sx={{ fontWeight: "bold", fontSize: "14px" }}
            />
          )}
        </Box>

        {/* TÍTULO PRINCIPAL CON INDICADOR DINÁMICO */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", margin: "0 30px 20px 30px" }}>
          <span style={{ fontSize: "25px" }}>
            <b>Panel de Auditoría de Inventario</b>
          </span>
          {tieneSugerenciaLoteSerie && (
            <Chip
              label="CONTIENE ARTÍCULOS CON SUGERENCIA DE LOTE/SERIE ACTIVA"
              color="warning"
              sx={{ mt: 1, fontWeight: "bold" }}
            />
          )}
        </div>

        <CustomBackdrop isLoading={isWorking} />

        <Box sx={StyledRoot}>
          {/* SECCIÓN CABECERA (READ-ONLY) */}
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom>
              Resumen de Planificación
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Número"
                  value={info.tomnumero || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="Descripción"
                  value={info.tomdescripcion || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Bodega"
                  value={info.bodcodigo || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Estado"
                  value={info.tomstatus || ""}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: true }}
                  size="small"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* NUEVA SECCIÓN: GESTIÓN DE CUADRILLA */}
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3, borderLeft: "5px solid #196C87" }}>
            <Typography
              variant="subtitle1"
              color="primary"
              fontWeight="bold"
              gutterBottom
              sx={{ display: "flex", alignItems: "center" }}
            >
              <GroupIcon sx={{ mr: 1 }} /> Cuadrilla de Operadores Asignados
            </Typography>
            <Grid container spacing={2} alignItems="center" mt={1}>
              {canModifyCuadrilla && (
                <>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={usuariosList}
                      getOptionLabel={(o) => o.label || ""}
                      value={operadorSeleccionado}
                      onChange={(e, nv) => setOperadorSeleccionado(nv)}
                      renderInput={(params) => <TextField {...params} label="Nuevo Operador a Asignar" size="small" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={handleAddOperador}
                      disabled={!operadorSeleccionado || isWorking}
                      startIcon={<PersonAddIcon />}
                    >
                      Añadir
                    </Button>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Box display="flex" flexWrap="wrap" gap={1.5} mt={1}>
                  {cuadrillaList.length === 0 && !isLoadingCuadrilla && (
                    <Typography variant="body2" color="textSecondary">
                      No se encontraron operadores asignados.
                    </Typography>
                  )}
                  {cuadrillaList.map((op) => (
                    <Chip
                      key={op.usrcodigo}
                      label={`${op.usrcodigo} - ${op.usrnombre}`}
                      color="primary"
                      variant="outlined"
                      onDelete={canModifyCuadrilla ? () => handleRemoveOperador(op.usrcodigo) : undefined}
                      sx={{ fontWeight: "bold" }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* SECCIÓN DATAGRID (REPORTE DE DIFERENCIAS) */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" color="primary" fontWeight="bold">
                Reporte de Diferencias y Conteo
              </Typography>
              {rowSelectionModel.length > 0 && (
                <Chip
                  label={`${rowSelectionModel.length} artículo(s) seleccionado(s) para ajustar`}
                  color="warning"
                  size="small"
                  sx={{ fontWeight: "bold" }}
                />
              )}
            </Box>

            <Box sx={{ height: 500, width: "100%" }}>
              <DataGrid
                rows={listaDiferencias}
                columns={columns}
                getRowId={(row) => row.artcodigo}
                density="compact"
                getRowHeight={() => "auto"}
                getEstimatedRowHeight={() => 50}
                sx={multilineGridSx}
                checkboxSelection
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(newSelection) => {
                  setRowSelectionModel(newSelection)
                }}
                disableRowSelectionOnClick
              />
            </Box>
          </Paper>
        </Box>

        {/* ================= MODAL DE AJUSTES CONTABLES ================= */}
        <Dialog open={modalAjusteOpen} onClose={() => setModalAjusteOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#f5f5f5", color: "primary.main", fontWeight: "bold" }}>
            Generación de Ajustes de Inventario
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Seleccione los códigos de transacción que el sistema utilizará para ajustar los{" "}
              <b>{rowSelectionModel.length} artículos</b> seleccionados.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Transacción para Ingresos (Sobrantes) *"
                  value={ajusteForm.tracodigo_ingreso}
                  onChange={(e) => setAjusteForm({ ...ajusteForm, tracodigo_ingreso: e.target.value })}
                  size="small"
                >
                  {transaccionesIngreso.map((op) => (
                    <MenuItem key={op.tracodigo} value={op.tracodigo}>
                      {op.tracodigo} - {op.tradescri}
                    </MenuItem>
                  ))}
                  <MenuItem value="ING-AJU">ING-AJU - INGRESO POR AJUSTE TOMA FISICA</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Transacción para Egresos (Faltantes) *"
                  value={ajusteForm.tracodigo_egreso}
                  onChange={(e) => setAjusteForm({ ...ajusteForm, tracodigo_egreso: e.target.value })}
                  size="small"
                >
                  {transaccionesEgreso.map((op) => (
                    <MenuItem key={op.tracodigo} value={op.tracodigo}>
                      {op.tracodigo} - {op.tradescri}
                    </MenuItem>
                  ))}
                  <MenuItem value="EGR-AJU">EGR-AJU - EGRESO POR AJUSTE TOMA FISICA</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observación / Glosa de Documentos *"
                  value={ajusteForm.observacion}
                  onChange={(e) => setAjusteForm({ ...ajusteForm, observacion: e.target.value.toUpperCase() })}
                  size="small"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setModalAjusteOpen(false)} color="inherit" disabled={isWorking}>
              Cancelar
            </Button>
            <Button variant="contained" color="warning" onClick={handleEjecutarAjustes} disabled={isWorking}>
              Generar Transacciones
            </Button>
          </DialogActions>
        </Dialog>

        {/* ================= MODAL DE DESGLOSE LOTES Y SERIES ================= */}
        <Dialog open={modalDetalleOpen} onClose={handleCloseDesglose} maxWidth="md" fullWidth>
          <DialogTitle sx={{ bgcolor: "#f5f5f5", color: "primary.main", fontWeight: "bold" }}>
            Desglose de Lotes y Series
          </DialogTitle>
          <DialogContent dividers sx={{ minHeight: 300, display: "flex", flexDirection: "column" }}>
            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">
                Artículo Seleccionado:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {articuloSeleccionado.codigo} - {articuloSeleccionado.descripcion}
              </Typography>
            </Box>

            <Box sx={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={listaDesglose}
                columns={columnsDesglose}
                getRowId={(row) => row.contsecuencia || row.id || Math.random().toString(36).substr(2, 9)}
                density="compact"
                loading={isFetchingDesglose}
                disableRowSelectionOnClick
                sx={{
                  "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f0f4f8", color: "#196C87" },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDesglose} variant="contained" color="inherit">
              Cerrar Desglose
            </Button>
          </DialogActions>
        </Dialog>

        {/* ================= MODAL HISTORIAL Y REVERSOS (AUDITORÍA) ================= */}
        <Dialog open={modalHistorialOpen} onClose={() => setModalHistorialOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#f5f5f5", color: "primary.main", fontWeight: "bold" }}>
            Historial Transaccional
            <Typography variant="subtitle2" color="textSecondary">
              Artículo: {articuloSeleccionado.codigo} - {articuloSeleccionado.descripcion}
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, minHeight: 200 }}>
            {isFetchingHistorial ? (
              <Box p={4} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : historialLecturas.length === 0 ? (
              <Box p={3} textAlign="center">
                <Typography color="textSecondary">
                  No existen lecturas registradas para este artículo en este tomo.
                </Typography>
              </Box>
            ) : (
              <List>
                {historialLecturas.map((item, index) => {
                  const isReverso = item.cantidad < 0 || String(item.origen_glosa).includes("REVERSO")

                  // Validación de CAPA 1: Revisar si la secuencia actual ya posee una compensación en la lista
                  const yaFueReversado = historialLecturas.some(
                    (h) => h.origen_glosa === `REVERSO DE SEC: ${item.contsecuencia}`,
                  )

                  return (
                    <React.Fragment key={index}>
                      <ListItem
                        sx={{ bgcolor: isReverso ? "#ffebee" : "inherit" }}
                        secondaryAction={
                          // REGLA: Si cantidad > 0 y es supervisor con tomo abierto
                          item.cantidad > 0 && canModifyCuadrilla ? (
                            yaFueReversado ? (
                              <Chip
                                label="Reversado"
                                color="error"
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: "bold" }}
                              />
                            ) : (
                              <Tooltip title="Reversar Lectura (Generar Transacción Compensatoria)">
                                <IconButton
                                  edge="end"
                                  color="error"
                                  onClick={() => handleEjecutarReverso(item.contsecuencia, item.cantidad)}
                                >
                                  <SettingsBackupRestoreIcon />
                                </IconButton>
                              </Tooltip>
                            )
                          ) : null
                        }
                      >
                        <ListItemText
                          primary={
                            <Typography fontWeight="bold" color={isReverso ? "error" : "primary"}>
                              Sec: {item.contsecuencia} <span style={{ marginLeft: 8 }}>{item.cantidad} und</span>
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" display="block">
                                Operador: {item.operador}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Fecha: {item.fecha_hora}
                              </Typography>
                              {item.origen_glosa && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color={isReverso ? "error" : "textSecondary"}
                                >
                                  Glosa: {item.origen_glosa}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  )
                })}
              </List>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setModalHistorialOpen(false)}
              color="inherit"
              fullWidth
              variant="contained"
              sx={{ bgcolor: "#e0e0e0", color: "black" }}
            >
              Cerrar Panel
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

export default DetalleTomaFisica
